// The recipe kit: the arithmetic every effect recipe draws with (R924).
//
// An effect is a slow visual animation a player wears on a fleet, a round or
// a hold. `effect-styles.ts` is the registry that answers a wire id;
// `effect-recipes-fleet.ts`, `effect-recipes-torpedo.ts` and
// `effect-recipes-hold.ts` hold the draw code of the picked cards of mockup
// rounds 041, 042 and 043. This module is what all four import: the row and
// the context types, the two clocks, the two hashes, the ink drift, the two
// alpha clamps, the atlas cell keys and the EIGHT FAMILIES.
//
// A family is the fallback: a catalog row with no recipe of its own draws
// its family, so every row of the catalog draws something.
//
// This is a leaf (`leaves.test.ts`). It names the three sinks of a layer
// through a type-only clause, the way `aura-styles.ts` does. The layer owns
// the state, the clock, the cull and the atlas, and it hands them in as one
// reused `EffectCtx`. The clamps and the cell scale every family shares stand
// in `recipe-kit.ts`; this module binds them to `[effects.limits]`.
//
// **Nothing here allocates.** A recipe runs once an object a frame, so
// every scratch is a module constant and every hash is integer arithmetic
// (R131).
//
// **A recipe is a pure function of the clock** (R915). It reads `ctx.t` and
// nothing else that moves, so a frozen `t` freezes the mark and leaves it
// on the water. That is the whole reduced-motion rule: a viewer who asked
// for less motion still sees the mark, and the layer stops the clock.

import { EFFECT_LIMITS } from '../gen/presentation';
import type { SpriteEntry } from '../fast/layers/batches';
import { TAU, cellKeys, cellScale, fillFor, frac, liftInk, strokeFor, type SinkCtx } from './recipe-kit';

export { TAU, bezier2, cellScale } from './recipe-kit';

/** The eight families a row may name. */
export type EffectFamily =
  'flare' | 'ring' | 'pulse' | 'drift' | 'fog' | 'radiant' | 'glitter' | 'trail';

/** Every family, in the order `effects.md` §1 lists them. */
export const EFFECT_FAMILIES: readonly EffectFamily[] = [
  'flare', 'ring', 'pulse', 'drift', 'fog', 'radiant', 'glitter', 'trail',
] as const;

/**
 * One row of the catalog, as the two halves join it
 * (`client/src/gen/presentation.ts` `EFFECTS`).
 *
 * The shape is stated structurally, so a test may hand in a row of its own
 * for a family the four shipping rows do not use.
 */
export interface EffectRow {
  readonly code: string;
  readonly wire: number;
  readonly targets: readonly string[];
  /** The ShipClass number a hull-scoped row is limited to; 0 = every hull. */
  readonly hull: number;
  /** The TorpedoKind number a kind-scoped row is limited to; 0 = every kind. */
  readonly torpedo: number;
  readonly family: string;
  readonly tint: string;
  /** The row's own ink, or null where the row wears the faction colour. */
  readonly ink: number | null;
  readonly periodSec: number;
  readonly radiusPx: number;
  readonly alpha: number;
  readonly count: number;
  /** A second radius: an orbit, an outer ring, a pip's glow, a hold's reach. */
  readonly radius2Px: number;
  /** A stroke width, or the widest grain of a scatter. */
  readonly widthPx: number;
  /** A trail, a strand or a rise, in world px. */
  readonly lengthPx: number;
  /** A second alpha: a stroke, or a resting glow. Held to the STROKE ceiling. */
  readonly alpha2: number;
  /** Revolutions a second for an orbit or a turn, or world px a second. */
  readonly speed: number;
  /** A second count: the strands of a pair, or the beads of one strand. */
  readonly count2: number;
}

/** What an effect is drawn on. */
export type EffectKind = 'fleet' | 'torpedo' | 'hold';

/**
 * What a recipe may draw with.
 *
 * The layer fills one of these in and hands the SAME object to every object
 * of the frame, so a recipe must read it and keep no reference to it. The
 * fade `alpha`, the additive flag, the three sinks and the atlas cell lookup
 * are the `SinkCtx` of the shared kit.
 */
export interface EffectCtx extends SinkCtx {
  /** What the mark is drawn on. */
  kind: EffectKind;
  /** The object, in world px. */
  x: number;
  y: number;
  /** The object's heading in radians. A hold has none and reads 0. */
  heading: number;
  /** The object's id, which fixes every hashed grain of the mark. */
  id: number;
  /** The layer's clock, in seconds. It is HELD under reduced motion. */
  t: number;
  /** The owner's phase, 0 to 1, so two owners never pulse in step. */
  phase: number;
  /** The ink, already resolved: the faction colour, or the row's own. */
  color: number;
  /**
   * How fast the object runs, in world px a second. Absent reads 0.
   *
   * A round is the one object that runs, and a mark it leaves "in the water"
   * needs the figure: the mark holds its place while the round goes on, so
   * the recipe puts it `speed * age` astern (R924). A fleet and a hold set
   * nothing, and a round recipe that gets 0 falls back to the `speed` figure
   * of its own row.
   */
  speed?: number;
  /**
   * A round's run progress, 0 to 1. Absent reads 0.
   *
   * It is the number `RoundSnap.u` carries, so a recipe may thin a mark over
   * the run without a clock of its own. A fleet and a hold set nothing.
   */
  u?: number;
  /** The camera scale: CSS px for one world px. */
  z: number;
}

/**
 * One family, or one picked card's own draw code keyed by the catalog code
 * (R924), drawn into the sinks the context carries.
 *
 * A card and a family share the shape, because a recipe reads the same row
 * and the same context: `effect-recipes-fleet.ts`, `effect-recipes-torpedo.ts`
 * and `effect-recipes-hold.ts` each export one map of these, and the
 * registry prefers the map over the family.
 */
export type EffectRecipeFn = (row: EffectRow, c: EffectCtx) => void;

// ---------------------------------------------------------------------------
// The atlas cells
// ---------------------------------------------------------------------------

/** The three marks a recipe draws from, all baked WHITE. */
const EFFECT_CELLS = ['disc', 'fog', 'glint'] as const;
export type EffectCell = (typeof EFFECT_CELLS)[number];
const EFFECT_CELL_KEYS = cellKeys('effect', EFFECT_CELLS);

/**
 * The atlas key of one baked effect cell.
 *
 * The scheme lives here and not in the atlas, because the leaf asks for the
 * key and `fast/gfx/atlas.ts` answers it: one spelling, two readers. It is
 * the rule `auraCellKey` follows.
 */
export function effectCellKey(cell: EffectCell): string {
  return EFFECT_CELL_KEYS[cell];
}

/**
 * The side of every baked effect cell, in world px at scale 1.
 *
 * One size serves every row: a recipe scales the quad to the radius its row
 * states, so a period, a radius or a count change bakes nothing new.
 */
export const EFFECT_CELL_PX = 64;

// ---------------------------------------------------------------------------
// The clocks and the hashes
// ---------------------------------------------------------------------------

/** The breath: `0.5 + 0.5 sin(...)`, 0 to 1, one whole turn a period. */
export function breathe(t: number, periodSec: number, phase: number): number {
  if (!(periodSec > 0)) return 1;
  return 0.5 + 0.5 * Math.sin(TAU * (t / periodSec + phase));
}

/** The saw: 0 to 1 over one period, then back to 0. */
export function cycle(t: number, periodSec: number, phase: number): number {
  if (!(periodSec > 0)) return 0;
  return frac(t / periodSec + phase);
}

/**
 * One owner's phase, 0 to 1.
 *
 * Every hull, round and hold of one account breathes in step, and two
 * accounts drift apart. The hash is integer arithmetic and allocates
 * nothing, so the layer may call it once an object a frame.
 */
export function ownerPhase(owner: number): number {
  return grain(owner, 0);
}

/**
 * A pure hash of an id and a grain index, 0 to 1.
 *
 * It is the scatter of every grain a `glitter` or a `trail` sheds: one
 * hull's grains sit where they sat last frame, and no closure and no random
 * stream is kept per object (R131).
 */
export function grain(id: number, k: number): number {
  let h = (id | 0) ^ Math.imul(k | 0, 0x9e3779b1);
  h = Math.imul(h ^ (h >>> 16), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/**
 * `color` moved toward white by `k`, 0 to 1: the `lift` of `marks.ts` (R830)
 * under a ceiling. On paper the lift moves toward the core ink instead
 * (`recipe-kit.ts` `LIFT_INK`, R3293).
 *
 * The `drift` family and three cards are the callers, and
 * `EFFECT_LIMITS.maxDriftTowardWhite` is the ceiling: a mark that reached
 * white would read as another faction's ink, which is the one thing a
 * cosmetic must never say.
 */
export function mixWhite(color: number, k: number): number {
  return liftInk(color, Math.max(0, Math.min(EFFECT_LIMITS.maxDriftTowardWhite, k)));
}

// ---------------------------------------------------------------------------
// The shape figures
// ---------------------------------------------------------------------------
//
// These are the proportions of one animation and not figures a designer
// moves: the row states the period, the radius, the alpha and the count,
// and the constants below are how a family spends them. They live here for
// the reason `fx.ts` keeps its own ages and ramps — they belong to the
// picture and not to the balance pack.

/** A stroke of one world px, which every ring and every ray takes. */
const STROKE_W = 1;
/** Where a `radiant` ray starts, as a fraction of the reach. */
const RAY_INNER = 0.5;
/** How wide a `glitter` glint is drawn, as a fraction of the reach. */
const GLINT_R = 0.12;
/** How wide a `trail` grain is drawn, as a fraction of the reach. */
const TRAIL_GRAIN_R = 0.09;
/** How far a `trail` grain wanders off the wake line, in grain radii. */
const TRAIL_WOBBLE = 1.5;
/** How far a `fog` blob wanders from the object, as a fraction of the reach. */
const FOG_WANDER = 0.35;
/** The floor a `fog` blob fades to, so a patch never blinks out. */
const FOG_FLOOR = 0.35;

/** A fill alpha, held under the ceiling the limits state. */
export const fill = fillFor(EFFECT_LIMITS);

/** A one-px stroke alpha, held under the ceiling the limits state. */
export const stroke = strokeFor(EFFECT_LIMITS);

/**
 * One soft additive cell in the mark's own ink, at the quad scale `s`.
 *
 * It is the stanza most recipes write: the fill clamp, the object's fade and
 * the additive flag are stated one time here.
 */
export function glow(
  c: EffectCtx, e: SpriteEntry, x: number, y: number, s: number, a: number,
): void {
  c.quads.sprite(x, y, 0, e, c.color, fill(a) * c.alpha, s, c.add);
}

/**
 * One hard disc in the mark's own ink. It is NOT additive, so a core over a
 * lit glow stays a core and does not go white-hot.
 */
export function core(c: EffectCtx, x: number, y: number, r: number, a: number): void {
  c.rings.disc(x, y, r, c.color, fill(a) * c.alpha);
}

// ---------------------------------------------------------------------------
// The eight families
// ---------------------------------------------------------------------------

export const EFFECT_STYLES: Record<EffectFamily, EffectRecipeFn> = {
  /**
   * Soft additive discs that breathe. One disc rests under the object; two
   * or more stand round it and light in sequence.
   */
  flare(row, c) {
    const e = c.sprite(effectCellKey('disc'));
    const s = cellScale(e, row.radiusPx);
    const off = row.count === 1 ? 0 : row.radiusPx;
    for (let k = 0; k < row.count; k++) {
      const a = breathe(c.t, row.periodSec, c.phase + k / row.count);
      const ang = c.heading + (k / row.count) * TAU;
      glow(c, e, c.x + Math.cos(ang) * off, c.y + Math.sin(ang) * off, s,
        row.alpha * a);
    }
  },

  /** Rings born at the object, grown to the reach and faded to nothing. */
  ring(row, c) {
    for (let k = 0; k < row.count; k++) {
      const u = cycle(c.t, row.periodSec, c.phase + k / row.count);
      c.rings.ring(c.x, c.y, u * row.radiusPx, STROKE_W, c.color,
        stroke(row.alpha * (1 - u)) * c.alpha);
    }
  },

  /** One additive disc over the object's own mark, breathing. */
  pulse(row, c) {
    const a = breathe(c.t, row.periodSec, c.phase);
    c.rings.disc(c.x, c.y, row.radiusPx, c.color,
      fill(row.alpha * a) * c.alpha, c.add);
  },

  /** One rim whose ink drifts toward white and back. */
  drift(row, c) {
    const k = EFFECT_LIMITS.maxDriftTowardWhite * breathe(c.t, row.periodSec, c.phase);
    c.rings.ring(c.x, c.y, row.radiusPx, STROKE_W, mixWhite(c.color, k),
      stroke(row.alpha) * c.alpha);
  },

  /** Faint blobs that wander round the object and never blink out. */
  fog(row, c) {
    const e = c.sprite(effectCellKey('fog'));
    const s = cellScale(e, row.radiusPx);
    const d = FOG_WANDER * row.radiusPx;
    for (let k = 0; k < row.count; k++) {
      const p = c.phase + grain(c.id, k + 1);
      const ang = cycle(c.t, row.periodSec, p) * TAU;
      const a = FOG_FLOOR + (1 - FOG_FLOOR) * breathe(c.t, row.periodSec, p);
      glow(c, e, c.x + Math.cos(ang) * d, c.y + Math.sin(ang) * d, s, row.alpha * a);
    }
  },

  /** A fan of rays that turns one whole revolution a period. */
  radiant(row, c) {
    const turn = cycle(c.t, row.periodSec, c.phase) * TAU;
    const r0 = RAY_INNER * row.radiusPx;
    for (let k = 0; k < row.count; k++) {
      const ang = turn + (k / row.count) * TAU;
      const cos = Math.cos(ang), sin = Math.sin(ang);
      c.segs.line(
        c.x + cos * r0, c.y + sin * r0,
        c.x + cos * row.radiusPx, c.y + sin * row.radiusPx,
        STROKE_W, c.color, stroke(row.alpha) * c.alpha,
      );
    }
  },

  /** Glints scattered inside the reach, each twinkling once a period. */
  glitter(row, c) {
    const e = c.sprite(effectCellKey('glint'));
    const s = cellScale(e, GLINT_R * row.radiusPx);
    for (let k = 0; k < row.count; k++) {
      const ang = grain(c.id, k + 1) * TAU;
      const d = Math.sqrt(grain(c.id, k + 101)) * row.radiusPx;
      // The negative half of the sine is cut, so a glint rests dark for
      // half of every period and the set never twinkles at once.
      const u = cycle(c.t, row.periodSec, c.phase + grain(c.id, k + 7));
      const a = Math.max(0, Math.sin(TAU * u));
      glow(c, e, c.x + Math.cos(ang) * d, c.y + Math.sin(ang) * d, s, row.alpha * a);
    }
  },

  /** Grains shed astern, thinning with the distance behind the object. */
  trail(row, c) {
    const e = c.sprite(effectCellKey('disc'));
    const s = cellScale(e, TRAIL_GRAIN_R * row.radiusPx);
    const cos = Math.cos(c.heading), sin = Math.sin(c.heading);
    const wobble = TRAIL_WOBBLE * TRAIL_GRAIN_R * row.radiusPx;
    for (let k = 0; k < row.count; k++) {
      const u = (k + 1) / row.count;
      const d = u * row.radiusPx;
      const w = (grain(c.id, k + 1) - 0.5) * 2 * wobble;
      const a = (1 - u) * breathe(c.t, row.periodSec, c.phase + grain(c.id, k + 31));
      glow(c, e, c.x - cos * d - sin * w, c.y - sin * d + cos * w, s, row.alpha * a);
    }
  },
};

/** True where the string names one of the eight families. */
export function isEffectFamily(v: string): v is EffectFamily {
  return (EFFECT_FAMILIES as readonly string[]).includes(v);
}
